import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { services } from '../mock';
import { useBooking } from '../hooks/useBooking';
import useSEO from '../hooks/useSEO';
import { ChevronRight, Check } from 'lucide-react';

const RichContent = ({ rich }) => (
  <>
    {rich.heading && (
      <p className="text-brand font-serif-display text-xl md:text-2xl mt-6 leading-snug">
        {rich.heading}
      </p>
    )}
    {rich.subheading && (
      <p className="text-brand-muted mt-3 italic leading-relaxed">{rich.subheading}</p>
    )}
    {rich.intro?.map((para, i) => (
      <p key={`intro-${i}`} className="text-ink-soft mt-4 leading-relaxed">
        {para}
      </p>
    ))}
    {rich.sections?.map((sec, si) => (
      <div key={`sec-${si}`}>
        <h4 className="section-eyebrow mt-8">{sec.title}</h4>
        {sec.blocks?.map((b, bi) => {
          if (b.type === 'p') {
            return (
              <p key={`b-${si}-${bi}`} className="text-ink-soft mt-3 leading-relaxed">
                {b.text}
              </p>
            );
          }
          if (b.type === 'label') {
            return (
              <p key={`b-${si}-${bi}`} className="text-brand mt-4 font-medium">
                {b.text}
              </p>
            );
          }
          if (b.type === 'ul') {
            return (
              <ul key={`b-${si}-${bi}`} className="mt-3 space-y-2">
                {b.items.map((it, ii) => (
                  <li key={`li-${si}-${bi}-${ii}`} className="flex gap-3 text-ink-soft">
                    <Check
                      className="w-4 h-4 text-gold mt-1.5 flex-shrink-0"
                      strokeWidth={2}
                    />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            );
          }
          return null;
        })}
      </div>
    ))}
  </>
);

const LegacyContent = ({ s }) => (
  <>
    <p className="text-ink-soft mt-6 leading-relaxed">{s.intro}</p>

    <h4 className="section-eyebrow mt-8">Põhieesmärgid</h4>
    <ul className="mt-4 space-y-2">
      {s.goals?.map((g, i) => (
        <li key={i} className="flex gap-3 text-ink-soft">
          <Check className="w-4 h-4 text-gold mt-1.5 flex-shrink-0" strokeWidth={2} />
          <span>{g}</span>
        </li>
      ))}
    </ul>

    <h4 className="section-eyebrow mt-8">Protseduur</h4>
    <p className="text-ink-soft mt-3">{s.procedure}</p>

    <h4 className="section-eyebrow mt-8">Kellele sobib</h4>
    <p className="text-ink-soft mt-3">{s.suitedFor}</p>

    <h4 className="section-eyebrow mt-8">Soovituslik kuur</h4>
    <p className="text-ink-soft mt-3">{s.course}</p>
  </>
);

const Services = () => {
  useSEO({
    title: 'Teenused — LPG, kavitatsioon, RF lifting | Prestige Beauty & Wellness',
    description:
      'Tutvu meie teenustega: LPG Alliance kehahooldused, LPG näomassaaž, kavitatsioon, klassikaline massaaž, inframatt ja RF lifting Pärnus.',
    path: '/teenused',
  });
  const navigate = useNavigate();
  const location = useLocation();
  const openBooking = useBooking();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <div>
      <section className="hero-bg pt-36 pb-20 px-6 text-center">
        <h1 className="section-title text-4xl md:text-5xl mt-4 fade-up">Teenused</h1>
        <div className="mt-6 flex justify-center fade-up"><span className="gold-divider" /></div>
      </section>

      <section className="bg-ivory py-20 px-6">
        <div className="max-w-5xl mx-auto space-y-32">
          {services.map((s, idx) => {
            if (s.placeholder) {
              return (
                <div
                  key={s.id}
                  id={s.id}
                  className="scroll-mt-28 text-center max-w-3xl mx-auto"
                >
                  <h2 className="font-serif-display text-3xl md:text-4xl text-brand">
                    {s.title}
                  </h2>
                  <div className="mt-4 flex justify-center">
                    <span className="gold-divider" />
                  </div>
                  <p className="text-brand-muted mt-6 italic">
                    Sisu on peagi saabumas.
                  </p>
                </div>
              );
            }

            // Consistent layout for every service: image on the left,
            // content on the right. No alternating.
            return (
              <div
                key={s.id}
                id={s.id}
                className="grid grid-cols-1 md:grid-cols-2 gap-14 items-start scroll-mt-28"
              >
                <div>
                  <div className="rounded-sm overflow-hidden shadow-lg border border-[var(--gold-soft)]">
                    <img
                      src={s.image}
                      alt={s.title}
                      className="w-full h-[420px] object-cover"
                    />
                  </div>
                  <div className="mt-6 text-center">
                    <div className="section-eyebrow">{s.tagline}</div>
                    <p className="text-brand-muted mt-3 text-sm italic">{s.tagDesc}</p>
                  </div>
                </div>
                <div>
                  <h2 className="font-serif-display text-3xl md:text-4xl text-brand">
                    {s.title}
                  </h2>
                  <div className="mt-4"><span className="gold-divider" /></div>

                  {s.richContent ? <RichContent rich={s.richContent} /> : <LegacyContent s={s} />}

                  <div className="flex flex-wrap gap-4 mt-10">
                    <button className="btn-gold-sm" onClick={openBooking}>
                      Broneeri aeg <ChevronRight className="w-4 h-4 ml-1" strokeWidth={1.5} />
                    </button>
                    <button className="btn-pill" onClick={() => navigate('/hinnakiri')}>
                      Hinnakiri
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default Services;
